// Call pop from UniFi Talk (ADR 0038). Talk 5.4.5 posts one JSON body per
// call event to the web Worker; Platform Core keeps the call log and decides
// who sees each call; the browser hears about a change over a WebSocket and
// fetches the pop it is allowed to see.
//
// What Talk sends (captured 2026-10-12, docs/adr/0038-call-pop-from-unifi-talk.md):
//   {"event":"call_started","call_id":"<uuid>","direction":"in","caller_number":"+13195550123",
//    "dialed_number":"+13197538111","timestamp":1791817418400}
// Inbound: caller_number is the outside number (E.164), dialed_number BRE's
// number (the line). caller_name (caller ID) only on the end events.
// Outbound: caller_number is the staff extension ("1001"), caller_name the
// Talk user, dialed_number the outside number. Timestamps are epoch ms.

export const CALLS_SYSTEM_SUB = 'system:calls'

// The header Talk sends with the shared secret ("Custom Header" in Talk's
// webhook settings). Lower-case: header names are case-insensitive.
export const TALK_WEBHOOK_HEADER = 'x-hub-talk-key'
export const TALK_WEBHOOK_SECRET_MIN = 32
// The one public address Talk's webhooks came from on 2026-10-12 (ASN 5056).
// Documented for TALK_WEBHOOK_ALLOWED_IPS; never assumed in code.
export const TALK_OBSERVED_SOURCE_IP = '207.177.122.81'

export const TALK_EVENTS = ['call_started', 'call_answered', 'call_completed', 'call_cancelled'] as const
export type TalkEvent = (typeof TALK_EVENTS)[number]
export const isTalkEvent = (value: unknown): value is TalkEvent => typeof value === 'string' && (TALK_EVENTS as readonly string[]).includes(value)

export const CALL_DIRECTIONS = ['in', 'out'] as const
export type CallDirection = (typeof CALL_DIRECTIONS)[number]

// ringing -> answered -> completed; ringing -> missed (inbound nobody
// answered, or outbound nobody picked up). completed and missed are final:
// an event that arrives late (answered after completed) fills in its time
// and never moves the state back.
export const CALL_STATES = ['ringing', 'answered', 'completed', 'missed'] as const
export type CallState = (typeof CALL_STATES)[number]
export const CALL_FINAL_STATES: readonly CallState[] = ['completed', 'missed']

// How Hub knows whose call it was. Never a guess: exactly one active person
// holds the number as a phone contact point, or a staff member said so
// (rule 7: phone numbers are evidence, never identity).
export const CALL_MATCH_METHODS = ['unique_contact', 'staff_confirmed'] as const
export type CallMatchMethod = (typeof CALL_MATCH_METHODS)[number]

export const CALLBACK_STATUSES = ['none', 'needed', 'done'] as const
export type CallbackStatus = (typeof CALLBACK_STATUSES)[number]

// What the web Worker hands core for one webhook. Strings are cut to size
// here; nothing is trusted beyond its shape.
export type TalkCallEvent = {
  event: string
  callId: string | null
  direction: string | null
  callerNumber: string | null
  callerName: string | null
  dialedNumber: string | null
  // Epoch milliseconds as Talk sent it, or null.
  timestamp: number | null
  receivedAt: string
  // The body exactly as received (at most 8 KB), kept on the event row.
  raw: string
}

const str = (value: unknown, max: number): string | null => (typeof value === 'string' && value.trim() ? value.trim().slice(0, max) : null)

// Null when the body isn't a JSON object at all.
export function parseTalkBody(text: string, receivedAt: string): TalkCallEvent | null {
  let body: unknown
  try {
    body = JSON.parse(text)
  } catch {
    return null
  }
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null
  const b = body as Record<string, unknown>
  const ts = typeof b.timestamp === 'number' && Number.isFinite(b.timestamp) ? Math.trunc(b.timestamp) : typeof b.timestamp === 'string' && /^\d{10,16}$/.test(b.timestamp) ? Number(b.timestamp) : null
  return {
    event: str(b.event, 60) ?? '',
    callId: str(b.call_id, 100),
    direction: str(b.direction, 10),
    callerNumber: str(b.caller_number, 40),
    callerName: str(b.caller_name, 120),
    dialedNumber: str(b.dialed_number, 40),
    timestamp: ts,
    receivedAt,
    raw: text.slice(0, 8192),
  }
}

// Talk's epoch milliseconds as ISO, or null when absent or absurd (more than
// a day away from when Hub received it, in which case Hub's time is used).
export function talkTime(timestamp: number | null, receivedAt: string): string {
  const received = Date.parse(receivedAt)
  if (timestamp === null || !Number.isFinite(timestamp) || Math.abs(timestamp - received) > 86_400_000) return new Date(received).toISOString()
  return new Date(timestamp).toISOString()
}

// An outside number as E.164 when it can be one (a 10-digit number is US),
// otherwise the digits as given (a short code, an extension). Null when
// there is nothing.
export function normalizeCallNumber(raw: string | null): string | null {
  if (!raw) return null
  const value = raw.trim()
  const digits = value.replace(/\D/g, '')
  if (!digits) return null
  if (value.startsWith('+') && digits.length >= 8 && digits.length <= 15) return `+${digits}`
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return digits.slice(0, 20)
}

// "+13195550123" -> "(319) 555-0123"; anything else as it is.
export function formatCallNumber(value: string | null): string {
  if (!value) return 'Unknown number'
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(value)
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : value
}

export const CALL_STATE_LABELS: Record<CallState, string> = { ringing: 'Ringing', answered: 'Answered', completed: 'Completed', missed: 'Missed' }

// ------------------------------------------------------------- the records

export type PhoneLineAudience = { id: string; kind: 'person' | 'department'; targetId: string; name: string; addedAt: string }

export type PhoneLine = {
  id: string
  number: string
  label: string
  active: boolean
  audience: PhoneLineAudience[]
  // Signed-in or not, the people who would see a pop on this line today
  // (active, holding calls.read).
  recipientCount: number
  updatedAt: string
  version: number
}

export type PhoneExtension = { id: string; extension: string; personId: string; personName: string; label: string | null; createdAt: string }

export type CallSettings = {
  // null keeps the call log for good (the default: it is a CRM record).
  retentionDays: number | null
  updatedAt: string | null
  updatedByName: string | null
}

export type CallPersonRef = { personId: string; displayName: string }

export type CallSummary = {
  id: string
  direction: CallDirection
  state: CallState
  lineId: string | null
  lineLabel: string | null
  lineNumber: string | null
  outsideNumber: string | null
  extension: string | null
  staff: CallPersonRef | null
  // Caller ID (inbound, end events only) or the Talk user (outbound).
  callerName: string | null
  startedAt: string
  answeredAt: string | null
  endedAt: string | null
  durationSeconds: number | null
  // Only when known (unique contact or confirmed); null otherwise.
  person: (CallPersonRef & { method: CallMatchMethod }) | null
  callbackStatus: CallbackStatus
  calledBackAt: string | null
  calledBackBy: string | null
  noteCount: number
}

export type CallNote = { id: string; body: string; authorName: string | null; createdAt: string }

// The pop: what one staff member may see about one call. `candidates` is
// empty without people.read (`detailsHidden` says why).
export type CallPop = {
  call: CallSummary
  candidates: (CallPersonRef & { status: 'active' })[]
  detailsHidden: boolean
  dismissed: boolean
  notes: CallNote[]
  // rememberNumber: may save this call's number to the person confirmed
  // (people.manage, and the call has a real outside number).
  can: { confirm: boolean; createPerson: boolean; rememberNumber: boolean; note: boolean }
}

// What a confirmation did with the call's number: added it to the person's
// record, found they already had it, wasn't asked to, or the call had no
// usable number (an extension).
export type CallNumberSaved = 'added' | 'already_held' | 'not_requested' | 'no_number'

// What the live channel sends: no names, no numbers. The browser fetches
// the pop itself, through its own session and capabilities.
export type CallPopSignal = { type: 'call'; callId: string; state: CallState; direction: CallDirection; at: string }

export type CallHookResult = {
  outcome: 'applied' | 'duplicate' | 'log_only' | 'rate_limited'
  callId: string | null
  state: CallState | null
  direction: CallDirection | null
  changed: boolean
  recipients: string[]
}

export const CALL_LIST_FILTERS = ['all', 'inbound', 'outbound', 'missed', 'callbacks'] as const
export type CallListFilter = (typeof CALL_LIST_FILTERS)[number]
export const CALL_PAGE_SIZE = 50
